import { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Box, Flex } from "rebass/styled-components";
import { IoClose } from "react-icons/io5";
import { FaCommentDots } from "react-icons/fa";
import { Link } from "react-router-dom";

import { analytics } from "MVVM/Analytics";
import { IClip } from "MVVM/@types";

interface IAllstarClipBubbleProps {
  clip: IClip;
}

export const isAllstarClip = (clip: IClip) =>
  clip.tags?.find((tag) => tag.key === "CS_Command")?.value === "!allstar";

export const AllstarClipBubble = ({ clip }: IAllstarClipBubbleProps) => {
  const [dismissed, setDismissed] = useState(false);

  const isAllstar = isAllstarClip(clip);

  useEffect(() => {
    if (isAllstar) {
      analytics.track("Allstar Clip Bubble - Impression", {
        shareId: clip.shareId,
      });
    }
  }, [isAllstar, clip.shareId]);

  if (!isAllstar || dismissed) return null;

  return (
    <Container
      to="/!allstar"
      onClick={() =>
        analytics.track("Allstar Clip Bubble - Clicked", {
          shareId: clip.shareId,
        })
      }
    >
      <IconWrap>
        <FaCommentDots size={15} />
      </IconWrap>
      <Message>Made in-game with !allstar</Message>
      <DismissButton
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          analytics.track("Allstar Clip Bubble - Dismissed", {
            shareId: clip.shareId,
          });
          setDismissed(true);
        }}
      >
        <IoClose size={14} />
      </DismissButton>
    </Container>
  );
};

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled(Link)`
  align-items: center;
  animation: ${slideUp} 0.3s ease-out;
  backdrop-filter: blur(6px);
  background: rgba(13, 20, 25, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  bottom: 56px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
  cursor: pointer;
  display: flex;
  gap: 8px;
  padding: 8px 14px 8px 10px;
  position: absolute;
  right: 12px;
  text-decoration: none;
  transition:
    background 0.2s,
    transform 0.2s;
  z-index: 5;

  &:hover {
    background: rgba(13, 20, 25, 0.92);
    transform: scale(1.03);
  }
`;

const IconWrap = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
  flex-shrink: 0;
`;

const Message = styled(Box)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: "Barlow", sans-serif;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`;

const DismissButton = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  flex-shrink: 0;
  margin-left: 2px;
  opacity: 0.6;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;
